import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogContent, IconButton, Typography } from '@mui/material';
import { Add as AddIcon, Close as CloseIcon, CreditCard, Remove as RemoveIcon } from '@mui/icons-material';
import axios from 'axios';
import config from '../config';
import { useAuth } from '../contexts/AuthContext';
import FlatRemovalTree, { FlatTreeProperty } from './FlatRemovalTree';

// One card to change a per-flat plan: more flats charge the saved card the full monthly price per flat; fewer means choosing which to delete.
const ManageFlatsDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  flatLimit: number;
  onChanged: () => Promise<void>;
}> = ({ open, onClose, flatLimit, onChanged }) => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [properties, setProperties] = useState<FlatTreeProperty[]>([]);
  const [flatCount, setFlatCount] = useState(0);
  const [target, setTarget] = useState(flatLimit);
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unitAmount, setUnitAmount] = useState<number | null>(null);
  const [nextBilling, setNextBilling] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [card, setCard] = useState<{ type: string; brand?: string; last4?: string; email?: string } | null>(null);
  const [openingPortal, setOpeningPortal] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTarget(flatLimit);
    setRemoved(new Set());
    setError(null);
    setConfirming(false);
    axios.get(`${config.apiUrl}/api/flats`, { headers })
      .then(res => { setProperties(res.data.properties || []); setFlatCount(res.data.flat_count || 0); })
      .catch(() => setError('Could not load your flats'));
    axios.get(`${config.apiUrl}/api/payments/flat-plan`, { headers })
      .then(res => {
        setUnitAmount(res.data.unit_amount);
        setNextBilling(res.data.next_billing_date);
        setCard(res.data.payment_method ?? null);
      })
      .catch(() => setError('Could not load the plan price'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, flatLimit, token]);

  const money = (pence: number) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(pence / 100);
  const diff = target - flatLimit;
  const billingDate = nextBilling ? new Date(nextBilling).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' }) : '';
  const chargeToday = unitAmount !== null ? money(unitAmount * diff) : '';
  const mustRemove = diff < 0 ? Math.max(0, flatCount - target) : 0;
  const stillToRemove = mustRemove - removed.size;

  const changeTarget = (next: number) => {
    setTarget(Math.min(flatLimit + 100, Math.max(1, next)));
    setRemoved(new Set());
    setConfirming(false);
  };
  const toggle = (key: string) => setRemoved(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key);
    else if (next.size < mustRemove) next.add(key);
    return next;
  });

  const handleClose = () => { if (!saving) onClose(); };

  // The Stripe billing portal is where cards are added or swapped
  const openPortal = async () => {
    setOpeningPortal(true);
    try {
      const res = await axios.post(`${config.apiUrl}/api/payments/create-portal-session`, {}, { headers });
      window.location.href = res.data.url;
    } catch {
      setError('Could not open the billing portal. Try again.');
      setOpeningPortal(false);
    }
  };

  const handleConfirm = async () => {
    setSaving(true);
    setError(null);
    try {
      if (diff > 0) {
        await axios.post(`${config.apiUrl}/api/flats/buy-extra`, { count: diff }, { headers });
      } else {
        const remove_units = Array.from(removed).map(key => {
          const [normalized_address, flat] = key.split('||');
          return { normalized_address, flat };
        });
        await axios.post(`${config.apiUrl}/api/flats/remove`, { flats: target, remove_units }, { headers });
      }
      await onChanged();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Could not update your plan. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const buttonLabel = diff > 0 ? `Add ${diff} flat${diff === 1 ? '' : 's'}`
    : diff < 0 ? `Keep ${target} flat${target === 1 ? '' : 's'}`
    : 'No change';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogContent sx={{ p: 4, position: 'relative' }}>
        <IconButton aria-label="Close" onClick={handleClose} sx={{ position: 'absolute', top: 8, right: 8 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
        {confirming ? (
          <Box sx={{ textAlign: 'center' }}>
            <CreditCard sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography variant="h5" component="h2" fontWeight={700} sx={{ mb: 2 }}>Confirm payment</Typography>
            <Typography sx={{ mb: 1 }}>
              You are about to add {diff} flat{diff === 1 ? '' : 's'} to your plan.
            </Typography>
            <Typography fontWeight={700} sx={{ fontSize: '1.25rem', mb: 0.5 }}>{chargeToday} today</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {card?.type === 'card' && card.brand
                ? `Charged to your ${card.brand.charAt(0).toUpperCase()}${card.brand.slice(1)} card ending ${card.last4}.`
                : card?.type === 'link' ? `Charged to your Stripe Link account${card.email ? ` (${card.email})` : ''}. Link is a saved-wallet option, so the card behind it is not shown here.`
                : 'Charged to your saved payment method.'}
            </Typography>
            <Button size="small" onClick={openPortal} disabled={openingPortal || saving} sx={{ mb: 2, textTransform: 'none' }}>
              Change payment method
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Then {unitAmount !== null ? money(unitAmount * target) : ''} / month from {billingDate || 'your next billing date'}.
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Button variant="contained" fullWidth size="large" onClick={handleConfirm} disabled={saving} sx={{ mb: 1 }}>
              {saving ? <CircularProgress size={24} color="inherit" /> : `Confirm and pay ${chargeToday}`}
            </Button>
            <Button fullWidth onClick={() => setConfirming(false)} disabled={saving}>Back</Button>
          </Box>
        ) : (
          <>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <CreditCard sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
          <Typography variant="h5" component="h2" fontWeight={700}>Manage flats</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Your plan covers {flatLimit} flat{flatLimit === 1 ? '' : 's'} and you use {flatCount}.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 1 }}>
          <IconButton aria-label="Fewer flats" onClick={() => changeTarget(target - 1)} disabled={target <= 1 || saving} sx={{ border: 1, borderColor: 'divider' }}>
            <RemoveIcon />
          </IconButton>
          <Box sx={{ textAlign: 'center', minWidth: 90 }}>
            <Typography variant="h4" fontWeight={700} component="div">{target}</Typography>
            <Typography variant="body2" color="text.secondary">flat{target === 1 ? '' : 's'}</Typography>
          </Box>
          <IconButton aria-label="More flats" onClick={() => changeTarget(target + 1)} disabled={saving} sx={{ border: 1, borderColor: 'divider' }}>
            <AddIcon />
          </IconButton>
        </Box>
        {unitAmount !== null && (
          <Box sx={{ textAlign: 'center', mt: 1, mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Each flat is {money(unitAmount)} / month</Typography>
            {diff > 0 && (
              <Typography fontWeight={700} sx={{ fontSize: '1.05rem' }}>Today: {chargeToday}</Typography>
            )}
            {diff !== 0 && (
              <Typography fontWeight={700} sx={{ fontSize: '1.05rem' }}>
                Next billing date{nextBilling ? ` ${billingDate}` : ''}: {money(unitAmount * target)} / month
              </Typography>
            )}
            {diff < 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                The change applies now. Stripe credits the unused part of this period to your next invoice.
              </Typography>
            )}
            {diff === 0 && (
              <Typography variant="body2" color="text.secondary">Use + or − to change how many flats are on your plan.</Typography>
            )}
          </Box>
        )}

        {mustRemove > 0 && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              Pick the {mustRemove} flat{mustRemove === 1 ? '' : 's'} to delete. Their certificates will be permanently deleted.
              {stillToRemove > 0 ? ` ${stillToRemove} more to choose.` : ''}
            </Alert>
            <FlatRemovalTree properties={properties} removed={removed} mustRemove={mustRemove} onToggle={toggle} />
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Button variant="contained" fullWidth size="large" onClick={() => (diff > 0 ? setConfirming(true) : handleConfirm())} disabled={saving || diff === 0 || stillToRemove > 0 || unitAmount === null}>
          {saving ? <CircularProgress size={24} color="inherit" /> : buttonLabel}
        </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ManageFlatsDialog;
